import { useEffect, useState } from 'react';

/** Never mount deferred widgets sooner than this after route paint, even on a fast, idle device. */
const MIN_QUIET_MS = 2500;

/**
 * Defer mounting until after route paint + a quiet period (reduces LCP/CLS from below-fold chunks).
 * requestIdleCallback alone fires almost immediately on a fast device, which put the footer, guru card
 * and their images in the same bandwidth window as the hero.
 */
export function useDeferredMount(delayMs = 4000) {
  const [mount, setMount] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let idleId = 0;
    let timeoutId = 0;

    const schedule = () => {
      const run = () => {
        if (!cancelled) setMount(true);
      };
      const quiet = Math.min(delayMs, MIN_QUIET_MS);
      timeoutId = window.setTimeout(() => {
        if (typeof window.requestIdleCallback === 'function') {
          idleId = window.requestIdleCallback(run, { timeout: Math.max(0, delayMs - quiet) });
        } else {
          run();
        }
      }, quiet);
    };

    const onPainted = () => schedule();
    window.addEventListener('daiv-route-painted', onPainted, { once: true });
    if (document.readyState === 'complete') {
      schedule();
    }

    return () => {
      cancelled = true;
      window.removeEventListener('daiv-route-painted', onPainted);
      if (idleId && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId);
      }
      clearTimeout(timeoutId);
    };
  }, [delayMs]);

  return mount;
}

/**
 * Defer lazy footer body until after first paint so mobile LCP/CLS are not blocked.
 * FABs mount separately via FooterFabStack.
 */
export default function useDeferredFooterMount(delayMs = 4000) {
  return useDeferredMount(delayMs);
}
